import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Type-only import — erased at build time, zero bundle cost.
// Runtime values are loaded lazily inside connectRoomForCall.
import type { ConnectionState as LKConnectionState, Room as LKRoom, RemoteParticipant, RemoteTrack } from "livekit-client";
import { api } from "../../lib/api";
import { type AudioRoute, getAvailableRoutes, isAndroidNative, setAudioRoute } from "../../lib/audioRoute";

type DirectCall = {
  id: string;
  callerId: string;
  calleeId: string;
  status: "ringing" | "answered" | "declined" | "canceled" | "missed" | "ended";
  roomName: string;
  createdAt?: string;
  answeredAt?: string | null;
  endedAt?: string | null;
  endReason?: "declined" | "caller_canceled" | "no_answer" | "completed" | "peer_disconnected" | string | null;
  answeredClientId?: string | null;
  callerClientId?: string | null;
};

type IncomingPayload = {
  call?: DirectCall;
  callerName?: string;
  spaceId?: string | null;
};

type StatePayload = {
  call?: DirectCall;
  callerName?: string;
  reconnectingUserId?: string;
  connectionRestoredUserId?: string;
  reconnectGraceMs?: number;
};

type UseDirectCallArgs = {
  socket: any;
  token: string;
  selfUserId?: string | null;
  livekitUrl?: string;
  // Returns true when an incoming call should not ring out loud — i.e. the DM
  // space is muted/hidden or notifications are globally muted. The overlay still
  // shows so the user can answer; only the ringtone is suppressed.
  isCallMuted?: (spaceId: string | null) => boolean;
};

type CallPhase = "idle" | "incoming" | "outgoing" | "in_call" | "ended";
type MediaState = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "failed";

function isTerminal(status?: string) {
  return status === "declined" || status === "canceled" || status === "missed" || status === "ended";
}

function endedMessage(call: DirectCall, selfUserId?: string | null) {
  if (call.endReason === "declined") return "Call declined.";
  if (call.endReason === "caller_canceled") {
    return call.callerId === selfUserId ? "Call canceled." : "Caller canceled the call.";
  }
  if (call.endReason === "no_answer") return "No answer.";
  if (call.endReason === "peer_disconnected") return "Call ended after the connection was lost.";
  return "Call ended.";
}

// Hanging up while audio is still connecting aborts the in-flight LiveKit
// connection, which rejects with a benign "user initiated disconnect"
// cancellation. That isn't a real failure — surfacing it would leave a stale
// notice that lingers into the next call's overlay — so we swallow it.
function isCancelError(e: any): boolean {
  const msg = String(e?.message || e || "").toLowerCase();
  const name = String(e?.name || "").toLowerCase();
  return name === "aborterror" || msg.includes("initiated disconnect");
}

export function getDirectCallClientId() {
  const key = "echo:direct-call-client-id";
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const next = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `call-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(key, next);
    return next;
  } catch {
    return `call-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

async function enableMicWithRetry(room: LKRoom, opts: any, attempts = 3) {
  let lastErr: any = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      await room.localParticipant.setMicrophoneEnabled(true, opts);
      return;
    } catch (e: any) {
      lastErr = e;
      const msg = String(e?.message || "").toLowerCase();
      const retryable = msg.includes("engine not connected") || msg.includes("within timeout");
      if (!retryable || i === attempts - 1) break;
      await new Promise((r) => setTimeout(r, 200 * (i + 1)));
    }
  }
  throw lastErr || new Error("Failed to enable microphone");
}

export function useDirectCall({ socket, token, selfUserId, livekitUrl, isCallMuted }: UseDirectCallArgs) {
  const [phase, setPhase] = useState<CallPhase>("idle");
  const [call, setCall] = useState<DirectCall | null>(null);
  const [peerName, setPeerName] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>("");
  const [needsAudioUnlock, setNeedsAudioUnlock] = useState(false);
  const [remoteCount, setRemoteCount] = useState(0);
  const [mediaState, setMediaState] = useState<MediaState>("idle");
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedInputId, setSelectedInputId] = useState<string | null>(null);
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [finalMessage, setFinalMessage] = useState("");
  const [peerReconnecting, setPeerReconnecting] = useState(false);
  const [audioRoute, setAudioRouteState] = useState<AudioRoute>('earpiece');
  const [availableRoutes, setAvailableRoutes] = useState<AudioRoute[]>([]);
  const isAndroidClient = useMemo(
    () => /android/i.test(typeof navigator !== "undefined" ? navigator.userAgent || "" : ""),
    []
  );

  const roomRef = useRef<LKRoom | null>(null);
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const callIdRef = useRef<string | null>(null);
  const deafenedRef = useRef(false);
  const endedTimerRef = useRef<number | null>(null);
  // Guards the hang-up tone so a call that reports "ended" more than once
  // (socket event + active-call resync) only chimes a single time.
  const hangupTonePlayedRef = useRef<string | null>(null);
  const clientIdRef = useRef<string | null>(null);
  const ringtoneRef = useRef<{ context: AudioContext; intervalId: number } | null>(null);
  const ringbackRef = useRef<{ context: AudioContext; intervalId: number } | null>(null);
  const isConnectingRef = useRef(false);
  // DM space of the current incoming call, captured from the socket payload so the
  // ringtone effect can consult `isCallMuted` (the `call` object has no space id).
  const incomingSpaceIdRef = useRef<string | null>(null);
  if (!clientIdRef.current && typeof window !== "undefined") clientIdRef.current = getDirectCallClientId();

  const canCall = useMemo(() => !!token && !!selfUserId && !!livekitUrl, [token, selfUserId, livekitUrl]);

  const clearEndedTimer = useCallback(() => {
    if (endedTimerRef.current === null) return;
    window.clearTimeout(endedTimerRef.current);
    endedTimerRef.current = null;
  }, []);

  const dismissEnded = useCallback(() => {
    clearEndedTimer();
    setFinalMessage("");
    setError("");
    setCall(null);
    setPhase("idle");
  }, [clearEndedTimer]);

  const stopRingtone = useCallback(() => {
    const ringtone = ringtoneRef.current;
    if (!ringtone) return;
    window.clearInterval(ringtone.intervalId);
    void ringtone.context.close().catch(() => {});
    ringtoneRef.current = null;
  }, []);

  const startRingtone = useCallback(() => {
    stopRingtone();
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;
    try {
      const context = new AudioContextCtor() as AudioContext;
      const chime = () => {
        if (context.state === "closed") return;
        const startAt = context.currentTime;
        for (const [delay, frequency] of [[0, 523.25], [0.18, 659.25]] as const) {
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = "sine";
          oscillator.frequency.value = frequency;
          gain.gain.setValueAtTime(0.0001, startAt + delay);
          gain.gain.exponentialRampToValueAtTime(0.12, startAt + delay + 0.025);
          gain.gain.exponentialRampToValueAtTime(0.0001, startAt + delay + 0.18);
          oscillator.connect(gain).connect(context.destination);
          oscillator.start(startAt + delay);
          oscillator.stop(startAt + delay + 0.2);
        }
      };
      ringtoneRef.current = { context, intervalId: window.setInterval(chime, 1800) };
      void context.resume().then(chime).catch(() => {});
    } catch {
      // Browser autoplay policies can prevent ringing until the app has been interacted with.
    }
  }, [stopRingtone]);

  const stopRingback = useCallback(() => {
    const rb = ringbackRef.current;
    if (!rb) return;
    window.clearInterval(rb.intervalId);
    void rb.context.close().catch(() => {});
    ringbackRef.current = null;
  }, []);

  const startRingback = useCallback(() => {
    stopRingback();
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;
    try {
      const context = new AudioContextCtor() as AudioContext;
      // Classic ringback: two 0.4s bursts at ~440 Hz separated by a short gap, then ~3s silence
      const ring = () => {
        if (context.state === "closed") return;
        const t = context.currentTime;
        for (const delay of [0, 0.5]) {
          const osc = context.createOscillator();
          const gain = context.createGain();
          osc.type = "sine";
          osc.frequency.value = 440;
          gain.gain.setValueAtTime(0.0001, t + delay);
          gain.gain.exponentialRampToValueAtTime(0.1, t + delay + 0.02);
          gain.gain.setValueAtTime(0.1, t + delay + 0.38);
          gain.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.42);
          osc.connect(gain).connect(context.destination);
          osc.start(t + delay);
          osc.stop(t + delay + 0.45);
        }
      };
      ringbackRef.current = { context, intervalId: window.setInterval(ring, 3500) };
      void context.resume().then(ring).catch(() => {});
    } catch {
      // Autoplay policy — silently ignore
    }
  }, [stopRingback]);

  // Short "call ended" cue — two quick descending blips so the user knows the
  // call hung up without needing an on-screen notice. One-shot: it spins up its
  // own AudioContext and closes it once the tone finishes.
  const playHangupTone = useCallback(() => {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;
    try {
      const context = new AudioContextCtor() as AudioContext;
      const t0 = context.currentTime;
      for (const [delay, frequency] of [[0, 480], [0.16, 400]] as const) {
        const osc = context.createOscillator();
        const gain = context.createGain();
        osc.type = "sine";
        osc.frequency.value = frequency;
        gain.gain.setValueAtTime(0.0001, t0 + delay);
        gain.gain.exponentialRampToValueAtTime(0.16, t0 + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + delay + 0.14);
        osc.connect(gain).connect(context.destination);
        osc.start(t0 + delay);
        osc.stop(t0 + delay + 0.16);
      }
      window.setTimeout(() => { void context.close().catch(() => {}); }, 600);
      void context.resume().catch(() => {});
    } catch {
      // Autoplay policy — silently ignore
    }
  }, []);

  const clearAudioEls = useCallback(() => {
    for (const el of audioElsRef.current.values()) {
      try { el.pause(); } catch { /* noop */ }
      try { el.srcObject = null; } catch { /* noop */ }
      try { el.remove(); } catch { /* noop */ }
    }
    audioElsRef.current.clear();
  }, []);

  const disconnectRoom = useCallback(() => {
    if (roomRef.current) {
      try { roomRef.current.disconnect(); } catch { /* noop */ }
      roomRef.current = null;
    }
    clearAudioEls();
    setRemoteCount(0);
    setNeedsAudioUnlock(false);
    setMediaState("disconnected");
  }, [clearAudioEls]);

  const attachRemoteAudio = useCallback((participant: RemoteParticipant, track: RemoteTrack) => {
    if (track.kind !== "audio") return;
    const pid = participant.identity;
    const existing = audioElsRef.current.get(pid);
    if (existing) {
      try { existing.remove(); } catch { /* noop */ }
      audioElsRef.current.delete(pid);
    }
    try {
      const el = track.attach() as HTMLAudioElement;
      el.autoplay = true;
      el.style.display = "none";
      document.body.appendChild(el);
      audioElsRef.current.set(pid, el);
      if (selectedOutputId && typeof el.setSinkId === "function") {
        void el.setSinkId(selectedOutputId).catch(() => {});
      }
      el.muted = deafenedRef.current;
      const p = el.play();
      if (p && typeof (p as Promise<void>).catch === "function") {
        (p as Promise<void>).catch(() => setNeedsAudioUnlock(true));
      }
    } catch {
      setNeedsAudioUnlock(true);
    }
  }, [selectedOutputId]);

  const refreshDevices = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setInputs(list.filter((device) => device.kind === "audioinput"));
      setOutputs(list.filter((device) => device.kind === "audiooutput"));
      const current = roomRef.current?.localParticipant.getTrackPublication("microphone" as any)?.track?.mediaStreamTrack;
      const deviceId = current?.getSettings?.().deviceId;
      if (deviceId && !selectedInputId) setSelectedInputId(deviceId);
    } catch {
      setError("Unable to read available audio devices");
    }
  }, [selectedInputId]);

  const applyOutputDevice = useCallback(async (sinkId: string | null) => {
    setSelectedOutputId(sinkId);
    for (const el of audioElsRef.current.values()) {
      try {
        if (typeof el.setSinkId === "function") await el.setSinkId(sinkId || "");
      } catch {
        setError("This output device could not be selected");
      }
    }
  }, []);

  const switchMicrophone = useCallback(async (deviceId: string | null) => {
    setSelectedInputId(deviceId);
    const room = roomRef.current;
    if (!room || muted) return;
    const opts: any = {
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
      autoGainControl: !isAndroidClient,
    };
    if (deviceId) opts.deviceId = deviceId;
    try {
      await enableMicWithRetry(room, opts);
    } catch (e: any) {
      setError(e?.message || "Failed to switch microphone");
    }
  }, [isAndroidClient, muted]);

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !muted;
    try {
      if (next) {
        await room.localParticipant.setMicrophoneEnabled(false);
      } else {
        const opts: any = {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          autoGainControl: !isAndroidClient,
        };
        if (selectedInputId) opts.deviceId = selectedInputId;
        await enableMicWithRetry(room, opts);
      }
      setMuted(next);
    } catch (e: any) {
      setError(e?.message || "Failed to change microphone state");
    }
  }, [isAndroidClient, muted, selectedInputId]);

  const toggleDeafen = useCallback(() => {
    const next = !deafened;
    deafenedRef.current = next;
    for (const el of audioElsRef.current.values()) {
      try { el.muted = next; } catch { /* noop */ }
    }
    setDeafened(next);
  }, [deafened]);

  const connectRoomForCall = useCallback(async (callId: string) => {
    if (!canCall || !livekitUrl) return;
    if (roomRef.current && callIdRef.current === callId) return;
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;
    disconnectRoom();
    setMediaState("connecting");
    setError("");
    try {
      const res = await api.postAuth("/calls/token", { callId, clientId: clientIdRef.current }, token);
      const lkToken = String(res?.token || "");
      if (!lkToken) throw new Error("Call token missing");
      // Lazy-load LiveKit — only downloaded when a call actually starts
      const { Room, RoomEvent, ConnectionState } = await import("livekit-client");
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;
      callIdRef.current = callId;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: any, participant: RemoteParticipant) => {
        attachRemoteAudio(participant, track);
        setRemoteCount(room.remoteParticipants.size);
      });
      room.on(RoomEvent.TrackUnsubscribed, (_track: RemoteTrack, _pub: any, participant: RemoteParticipant) => {
        const el = audioElsRef.current.get(participant.identity);
        if (el) {
          try { el.pause(); } catch { /* noop */ }
          try { el.remove(); } catch { /* noop */ }
          audioElsRef.current.delete(participant.identity);
        }
        setRemoteCount(room.remoteParticipants.size);
      });
      room.on(RoomEvent.ParticipantConnected, () => setRemoteCount(room.remoteParticipants.size));
      room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        const el = audioElsRef.current.get(participant.identity);
        if (el) {
          try { el.pause(); } catch { /* noop */ }
          try { el.remove(); } catch { /* noop */ }
          audioElsRef.current.delete(participant.identity);
        }
        setRemoteCount(room.remoteParticipants.size);
      });
      room.on(RoomEvent.Disconnected, () => {
        disconnectRoom();
      });
      room.on(RoomEvent.Reconnecting, () => setMediaState("reconnecting"));
      room.on(RoomEvent.Reconnected, () => setMediaState("connected"));
      room.on(RoomEvent.ConnectionStateChanged, (state: LKConnectionState) => {
        if (state === ConnectionState.Connecting) setMediaState("connecting");
        if (state === ConnectionState.Connected) setMediaState("connected");
        if (state === ConnectionState.Reconnecting || state === ConnectionState.SignalReconnecting) setMediaState("reconnecting");
        if (state === ConnectionState.Disconnected) setMediaState("disconnected");
      });

      await room.connect(livekitUrl, lkToken, { autoSubscribe: true });
      const micCaptureOpts: any = {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
        autoGainControl: !isAndroidClient,
      };
      if (selectedInputId) micCaptureOpts.deviceId = selectedInputId;
      await enableMicWithRetry(room, micCaptureOpts);
      try { await room.startAudio(); setNeedsAudioUnlock(false); } catch { setNeedsAudioUnlock(true); }
      setMuted(false);
      setMediaState("connected");
      setRemoteCount(room.remoteParticipants.size);
    } catch (e: any) {
      disconnectRoom();
      // A hang-up mid-connect aborts the LiveKit connection — that's expected
      // teardown, not a failure to report (see isCancelError).
      if (!isCancelError(e)) {
        setMediaState("failed");
        setError(e?.message || "Failed to connect call audio");
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [attachRemoteAudio, canCall, disconnectRoom, isAndroidClient, livekitUrl, selectedInputId, token]);

  const applyCallState = useCallback((nextCall: DirectCall, callerName?: string) => {
    if (!selfUserId) return;
    const isCaller = nextCall.callerId === selfUserId;
    const isCallee = nextCall.calleeId === selfUserId;
    if (!isCaller && !isCallee) return;
    if (isCaller && nextCall.callerClientId && nextCall.callerClientId !== clientIdRef.current) return;

    setCall(nextCall);
    if (callerName && !isCaller) setPeerName(callerName);
    if (isTerminal(nextCall.status)) {
      clearEndedTimer();
      setPhase("ended");
      setError("");
      // Only a genuine hang-up of a connected call ("ended") gets the tone;
      // declined/canceled/missed have their own context and stay silent.
      if (nextCall.status === "ended" && hangupTonePlayedRef.current !== nextCall.id) {
        hangupTonePlayedRef.current = nextCall.id;
        playHangupTone();
      }
      setFinalMessage(endedMessage(nextCall, selfUserId));
      setPeerReconnecting(false);
      disconnectRoom();
      callIdRef.current = null;
      setMediaState("idle");
      setMuted(false);
      deafenedRef.current = false;
      setDeafened(false);
      setDevicesOpen(false);
      endedTimerRef.current = window.setTimeout(() => {
        setFinalMessage("");
        setCall(null);
        setPhase("idle");
        endedTimerRef.current = null;
      }, 5000);
      return;
    }
    clearEndedTimer();
    setFinalMessage("");
    if (nextCall.status === "ringing") {
      // A fresh ring should never carry an error from the previous call.
      setError("");
      setPeerReconnecting(false);
      setPhase(isCaller ? "outgoing" : "incoming");
      return;
    }
    if (nextCall.status === "answered") {
      const assignedClientId = isCaller ? nextCall.callerClientId : nextCall.answeredClientId;
      if (assignedClientId && assignedClientId !== clientIdRef.current) {
        clearEndedTimer();
        disconnectRoom();
        setFinalMessage("Answered on another device.");
        setPhase("ended");
        endedTimerRef.current = window.setTimeout(() => {
          setFinalMessage("");
          setCall(null);
          setPhase("idle");
          endedTimerRef.current = null;
        }, 4000);
        return;
      }
      setPhase("in_call");
      void connectRoomForCall(nextCall.id);
    }
  }, [clearEndedTimer, connectRoomForCall, disconnectRoom, playHangupTone, selfUserId]);

  useEffect(() => {
    if (phase === "incoming") {
      // Show the overlay but stay silent when the caller's DM (or all notifications) is muted.
      if (isCallMuted?.(incomingSpaceIdRef.current)) stopRingtone();
      else startRingtone();
    } else {
      stopRingtone();
    }
    return () => stopRingtone();
  }, [phase, call?.id, startRingtone, stopRingtone, isCallMuted]);

  useEffect(() => {
    if (phase === "outgoing") startRingback();
    else stopRingback();
    return () => stopRingback();
  }, [phase, call?.id, startRingback, stopRingback]);

  useEffect(() => {
    if (phase !== "in_call" || !call?.answeredAt) {
      setDurationSeconds(0);
      return;
    }
    const startedAt = new Date(call.answeredAt).getTime();
    const update = () => setDurationSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    update();
    const intervalId = window.setInterval(update, 1000);
    return () => window.clearInterval(intervalId);
  }, [call?.answeredAt, phase]);

  useEffect(() => {
    if (phase === "in_call" && devicesOpen) void refreshDevices();
  }, [devicesOpen, phase, refreshDevices]);

  const syncActiveCall = useCallback(async () => {
    if (!canCall) return;
    try {
      const res = await api.getAuth("/calls/active", token);
      const c = (res?.call || null) as DirectCall | null;
      const peer = typeof res?.peerName === "string" ? res.peerName : "";
      if (!c) {
        if (roomRef.current) return;
        clearEndedTimer();
        disconnectRoom();
        callIdRef.current = null;
        setCall(null);
        setPhase("idle");
        setMediaState("idle");
        setPeerReconnecting(false);
        setFinalMessage("");
        return;
      }
      if (peer) setPeerName(peer);
      applyCallState(c, peer || undefined);
    } catch {
      // noop: realtime state updates still apply through socket events
    }
  }, [applyCallState, canCall, clearEndedTimer, disconnectRoom, token]);

  useEffect(() => {
    if (!socket) return;
    const onIncoming = (payload: IncomingPayload) => {
      const c = payload?.call;
      if (!c) return;
      if (selfUserId && c.calleeId !== selfUserId) return;
      incomingSpaceIdRef.current = payload?.spaceId ? String(payload.spaceId) : null;
      setPeerName(payload?.callerName || "Incoming caller");
      applyCallState(c, payload?.callerName);
    };
    const onState = (payload: StatePayload) => {
      const c = payload?.call;
      if (!c) return;
      if (payload.reconnectingUserId && payload.reconnectingUserId !== selfUserId) {
        setPeerReconnecting(true);
      }
      if (payload.connectionRestoredUserId && payload.connectionRestoredUserId !== selfUserId) {
        setPeerReconnecting(false);
      }
      applyCallState(c, payload?.callerName);
    };
    socket.on("call:incoming", onIncoming);
    socket.on("call:state", onState);
    return () => {
      socket.off("call:incoming", onIncoming);
      socket.off("call:state", onState);
    };
  }, [socket, selfUserId, applyCallState]);

  useEffect(() => {
    if (!canCall) return;
    void syncActiveCall();
  }, [canCall, syncActiveCall]);

  useEffect(() => {
    if (!socket || !canCall) return;
    const onConnect = () => { void syncActiveCall(); };
    socket.on("connect", onConnect);
    return () => {
      socket.off("connect", onConnect);
    };
  }, [socket, canCall, syncActiveCall]);

  useEffect(() => {
    return () => {
      clearEndedTimer();
      stopRingtone();
      stopRingback();
      disconnectRoom();
    };
  }, [clearEndedTimer, disconnectRoom, stopRingtone, stopRingback]);

  const startCall = useCallback(async (calleeId: string, calleeName?: string) => {
    if (!canCall) return false;
    const targetId = String(calleeId || "").trim();
    if (!targetId) return false;
    setBusy(true);
    setError("");
    dismissEnded();
    try {
      const res = await api.postAuth("/calls/start", { calleeId: targetId, clientId: clientIdRef.current }, token);
      const c = (res?.call || null) as DirectCall | null;
      if (!c) throw new Error("Call did not start");
      if (calleeName) setPeerName(calleeName);
      applyCallState(c);
      return true;
    } catch (e: any) {
      setError(e?.message || "Failed to start call");
      return false;
    } finally {
      setBusy(false);
    }
  }, [applyCallState, canCall, dismissEnded, token]);

  const answerCall = useCallback(async () => {
    if (!call?.id) return;
    const callId = call.id;
    setBusy(true);
    setError("");
    try {
      const res = await api.postAuth("/calls/answer", { callId, clientId: clientIdRef.current }, token);
      const c = (res?.call || null) as DirectCall | null;
      if (!c) throw new Error("Failed to answer call");
      applyCallState(c);
      // Close the OS push notification so its sound stops
      try {
        navigator.serviceWorker?.controller?.postMessage({ type: 'CLOSE_CALL_NOTIFICATION', data: { callId } });
      } catch { /* noop */ }
    } catch (e: any) {
      setError(e?.message || "Failed to answer call");
    } finally {
      setBusy(false);
    }
  }, [applyCallState, call?.id, token]);

  const declineCall = useCallback(async () => {
    if (!call?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.postAuth("/calls/decline", { callId: call.id }, token);
      const c = (res?.call || null) as DirectCall | null;
      if (c) applyCallState(c);
      else {
        setPhase("idle");
        setCall(null);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to decline call");
    } finally {
      setBusy(false);
    }
  }, [applyCallState, call?.id, token]);

  const cancelCall = useCallback(async () => {
    if (!call?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.postAuth("/calls/cancel", { callId: call.id }, token);
      const c = (res?.call || null) as DirectCall | null;
      if (c) applyCallState(c);
      else {
        setPhase("idle");
        setCall(null);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to cancel call");
    } finally {
      setBusy(false);
    }
  }, [applyCallState, call?.id, token]);

  const endCall = useCallback(async () => {
    if (!call?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.postAuth("/calls/end", { callId: call.id }, token);
      const c = (res?.call || null) as DirectCall | null;
      if (c) applyCallState(c);
      else {
        setPhase("idle");
        setCall(null);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to end call");
    } finally {
      setBusy(false);
    }
  }, [applyCallState, call?.id, token]);

  const unlockAudio = useCallback(async () => {
    try {
      if (!roomRef.current) return;
      await roomRef.current.startAudio();
      for (const el of audioElsRef.current.values()) {
        try { await el.play(); } catch { /* noop */ }
      }
      setNeedsAudioUnlock(false);
    } catch {
      setNeedsAudioUnlock(true);
    }
  }, []);

  const toggleDevices = useCallback(() => setDevicesOpen((open) => !open), []);

  const changeAudioRoute = useCallback((route: AudioRoute) => {
    setAudioRoute(route);
    setAudioRouteState(route);
  }, []);

  // Initialise available routes when the call connects (Android only)
  useEffect(() => {
    if (!isAndroidNative()) return;
    const routes = getAvailableRoutes();
    if (routes.length > 0) setAvailableRoutes(routes);
  }, []);

  return {
    phase,
    call,
    peerName,
    busy,
    error,
    remoteCount,
    needsAudioUnlock,
    mediaState,
    muted,
    deafened,
    devicesOpen,
    inputs,
    outputs,
    selectedInputId,
    selectedOutputId,
    durationSeconds,
    finalMessage,
    peerReconnecting,
    canCall,
    startCall,
    answerCall,
    declineCall,
    cancelCall,
    endCall,
    unlockAudio,
    toggleMute,
    toggleDeafen,
    toggleDevices,
    refreshDevices,
    switchMicrophone,
    applyOutputDevice,
    dismissEnded,
    audioRoute,
    availableRoutes,
    changeAudioRoute,
  };
}
